/* CatSketch size guide.
   A true-scale wall elevation (1 SVG unit = 1 inch): floor, 8 ft ceiling, a piece of furniture, and the chosen canvas
   hung by the same rule as the room view and the size-picker glyph (centre at least 59 in from the floor, bottom edge
   at least 6.5 in above the furniture). Used on /pages/size-guide (mode "page") and as a sheet on canvas pages (mode "sheet"). */
(() => {
  if (window.csSizeGuide) return;
  const CEIL = 96, CENTRE = 59, GAP = 6.5;
  const FURN = [
    ['sofa', 'Sofa', 84, 32.3, 'an 84 in sofa', 'the sofa'],
    ['bed', 'Bed', 64, 48, 'a queen bed with a 48 in headboard', 'the headboard'],
    ['dresser', 'Dresser', 60, 34, 'a 60 in dresser', 'the dresser'],
    ['console', 'Console', 48, 30, 'a 48 in console table', 'the console'],
    ['nightstand', 'Nightstand', 22, 26, 'a nightstand', 'the nightstand'],
    ['wall', 'Open wall', 0, 0, 'an open wall', '']
  ].map(([k, label, w, top, desc, the]) => ({ k, label, w, top, desc, the }));
  const PICKS = { sofa: ['24x36', '32x48', '24x18'], bed: ['16x24', '24x36', '24x18'], dresser: ['16x24', '24x36', '24x18'],
    console: ['12x18', '16x24', '16x12', '24x18'], nightstand: ['8x10', '12x18', '16x12'], wall: ['24x36', '32x48', '24x18'] };
  const FRAMES = { 'Black frame': '#1f1d1b', 'White frame': '#f1eee8', 'Brown frame': '#5a3b26' };
  const cm = v => Math.round(v * 2.54);
  const parse = s => { const m = String(s || '').match(/(\d+(?:\.\d+)?)\D+?(\d+(?:\.\d+)?)/); return m ? [+m[1], +m[2]] : null; };
  const kOf = s => { const d = parse(s); return d ? d[0] + 'x' + d[1] : ''; };
  const nice = s => { const d = parse(s); return d ? `${d[0]} × ${d[1]}` : String(s); };
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const r1 = v => Math.round(v * 10) / 10;
  const half = v => Math.round(v * 2) / 2;
  const ease = t => 1 - Math.pow(1 - t, 3);
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- drawing (y up, converted to SVG y down) ---------- */
  const R = (x, y, w, h, r = 0, c = '') => `<rect class="${c}" x="${r1(x)}" y="${r1(-(y + h))}" width="${r1(w)}" height="${r1(h)}" rx="${r}"/>`;
  const C = (x, y, r, c = '') => `<circle class="${c}" cx="${x}" cy="${-y}" r="${r}"/>`;
  const E = (x, y, rx, ry, c = '') => `<ellipse class="${c}" cx="${x}" cy="${-y}" rx="${rx}" ry="${ry}"/>`;
  const L = (x1, y1, x2, y2, c = 's') => `<line class="${c}" x1="${x1}" y1="${-y1}" x2="${x2}" y2="${-y2}"/>`;
  const P = (pts, c = '') => `<polygon class="${c}" points="${pts.map(([x, y]) => `${x},${-y}`).join(' ')}"/>`;
  const books = (x, y) => R(x, y, 8, 1.3, .2, 'b1') + R(x + .5, y + 1.3, 7, 1.1, .2, 'b2') + R(x + .2, y + 2.4, 7.5, 1.2, .2, 'b3');
  const vase = (x, y) => L(x, y + 6.6, x + 2.2, y + 13.5) + L(x, y + 6.6, x - 2.4, y + 12.4) + L(x, y + 6.6, x + .4, y + 15) +
    E(x, y + 2.6, 2.2, 2.6, 'v') + R(x - .7, y + 4.6, 1.4, 2.2, .3, 'v');
  const lamp = (x, y) => E(x, y + 4, 3, 4, 'lb') + L(x, y + 8, x, y + 11) + P([[x - 6, y + 11], [x + 6, y + 11], [x + 4.4, y + 20], [x - 4.4, y + 20]], 'ls');
  function furn(k) {
    if (k === 'sofa') {
      const h = 42;
      return [R(-h + 5, 0, 2.2, 4.2, .6, 'w'), R(h - 7.2, 0, 2.2, 4.2, .6, 'w'),
        R(-h + 7, 16.5, 70, 15.8, 3, 'f'), L(0, 21, 0, 31.5),
        R(-h + 6, 4, 72, 13, 2, 'f'),
        R(-h + 8, 15.5, 33.5, 5.5, 2.2, 'f'), R(.5, 15.5, 33.5, 5.5, 2.2, 'f'),
        R(-h + 11, 20.5, 13, 11, 3.5, 'p'), R(h - 24, 20.5, 13, 11, 3.5, 'p2'),
        R(-h, 4, 8.5, 21, 3.6, 'f'), R(h - 8.5, 4, 8.5, 21, 3.6, 'f')].join('');
    }
    if (k === 'bed') {
      return [R(-32, 0, 64, 48, 5, 'f'), L(-16, 33, -16, 45), L(0, 33, 0, 45), L(16, 33, 16, 45),
        R(-27, 22, 25, 10, 4, 'p3'), R(2, 22, 25, 10, 4, 'p3'),
        R(-31.5, 12, 63, 12, 3, 'd'), R(-30.5, 3.5, 61, 9, 1.4, 'f'),
        R(-29.5, 0, 2.4, 3.6, .5, 'w'), R(27.1, 0, 2.4, 3.6, .5, 'w')].join('');
    }
    if (k === 'dresser') {
      let s = R(-28.5, 0, 2, 3.4, .4, 'w') + R(26.5, 0, 2, 3.4, .4, 'w') + R(-30, 3, 60, 30, 1, 'o');
      for (let i = 0; i < 3; i++) { const y = 4.3 + i * 9.4; s += R(-28.6, y, 57.2, 8.4, .7, 'o2') + C(-14, r1(y + 4.2), .7, 'k') + C(14, r1(y + 4.2), .7, 'k'); }
      return s + R(-30.6, 32.8, 61.2, 1.3, .4, 'o') + vase(21, 34.1) + books(-25, 34.1);
    }
    if (k === 'console') {
      return R(-23.2, 0, 1.3, 28.6, .3, 'w') + R(21.9, 0, 1.3, 28.6, .3, 'w') + R(-22.5, 7.5, 45, 1.2, .3, 'o') +
        R(-18, 8.7, 10, 6, 1.2, 'bk') + R(-24, 28.5, 48, 1.6, .5, 'o') + books(13, 30.1);
    }
    if (k === 'nightstand') {
      return R(-10, 0, 1.4, 5, .3, 'w') + R(8.6, 0, 1.4, 5, .3, 'w') + R(-11, 5, 22, 20.2, 1, 'o') + R(-11.4, 25, 22.8, 1, .3, 'o') +
        R(-9.6, 16.4, 19.2, 7, .6, 'o2') + C(0, 19.9, .6, 'k') + lamp(-4.5, 26);
    }
    return '';
  }
  const BOX = { sofa: [84, 33], bed: [64, 48], dresser: [62, 49], console: [48, 34], nightstand: [23, 46], wall: [0, 0] };
  function icon(k) {
    if (k === 'wall') return `<svg viewBox="-12 -21 24 22" aria-hidden="true" focusable="false"><rect class="ic-a" x="-5" y="-17" width="10" height="13" rx=".6"/><line class="s" x1="-12" y1="0" x2="12" y2="0"/></svg>`;
    const [w, h] = BOX[k], pad = 3;
    return `<svg viewBox="${-w / 2 - pad} ${-(h + pad)} ${w + pad * 2} ${h + pad + 1}" aria-hidden="true" focusable="false">${furn(k)}</svg>`;
  }

  /* ---------- styles (once) ---------- */
  const CSS = `
.csg{--ink:#2c2722;--mute:#6f655b;--line:rgba(44,39,34,.16);--paper:#f7f3ee;--chip:#fff;color:var(--ink);font-family:var(--font-body-family,inherit)}
.csg *{box-sizing:border-box}
.csg__grid{display:grid;gap:18px}
@media (min-width:990px){.csg[data-csg=page] .csg__grid{grid-template-columns:minmax(0,1.45fr) minmax(330px,1fr);gap:36px;align-items:start}
 .csg[data-csg=page] .csg__stage{position:sticky;top:96px}}
.csg__stage{position:relative;border-radius:14px;overflow:hidden;background:#e6ddd1;box-shadow:0 1px 0 rgba(255,255,255,.6) inset,0 18px 40px -24px rgba(44,39,34,.45)}
.csg__svg{display:block;width:100%;aspect-ratio:5/4;touch-action:pan-y}
@media (min-width:750px){.csg__svg{aspect-ratio:16/11}}
.csg[data-csg=sheet] .csg__svg{aspect-ratio:5/4}
@media (min-width:750px){.csg[data-csg=sheet] .csg__svg{aspect-ratio:16/10}}
.csg__cmpbtn{position:absolute;right:10px;bottom:8px;font:inherit;font-size:12.5px;line-height:1;border:1px solid rgba(44,39,34,.25);background:rgba(247,243,238,.92);color:var(--ink);border-radius:999px;padding:7px 11px;cursor:pointer;box-shadow:0 2px 8px -4px rgba(0,0,0,.4)}
.csg__cmpbtn[aria-pressed=true]{background:var(--ink);color:#f7f3ee;border-color:var(--ink)}
.csg__panel{display:grid;gap:14px;min-width:0}
.csg__row{min-width:0}
.csg__lab{font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:var(--mute);margin:0 0 7px}
.csg__chips{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;padding:9px 2px 4px;margin:-7px -2px 0;-webkit-overflow-scrolling:touch}
.csg__chips::-webkit-scrollbar{display:none}
@media (min-width:990px){.csg[data-csg=page] .csg__chips{flex-wrap:wrap;overflow:visible}}
@media (min-width:750px){.csg[data-csg=sheet] .csg__chips{flex-wrap:wrap;overflow:visible}}
.csg__chip{flex:0 0 auto;display:inline-flex;align-items:center;gap:7px;font:inherit;font-size:14px;line-height:1.15;color:var(--ink);background:var(--chip);border:1px solid var(--line);border-radius:999px;padding:8px 13px;cursor:pointer;transition:background .2s,border-color .2s,color .2s;position:relative}
.csg__chip:hover{border-color:rgba(44,39,34,.45)}
.csg__chip[aria-pressed=true]{background:var(--ink);border-color:var(--ink);color:#f7f3ee}
.csg__chip[disabled]{opacity:.38;cursor:not-allowed}
.csg__chip small{display:block;font-size:11.5px;opacity:.7}
.csg__chip--sz{flex-direction:column;align-items:flex-start;gap:1px;border-radius:12px;padding:7px 12px}
.csg__chip--fu{padding:6px 12px 6px 8px}
.csg__chip--fu svg{width:34px;height:22px;flex:0 0 auto}
.csg__chip .pick{position:absolute;top:-7px;right:6px;font-size:10px;letter-spacing:.04em;background:#5a3b26;color:#f7f3ee;border-radius:999px;padding:2px 6px}
.csg__sw{width:14px;height:14px;border-radius:3px;border:1px solid rgba(44,39,34,.35);flex:0 0 auto}
.csg__sw--u{background:linear-gradient(135deg,#e9e1d6 45%,#b9ab99 45% 55%,#e9e1d6 55%)}
.csg__out{background:#fff;border:1px solid var(--line);border-radius:14px;padding:14px 16px}
.csg__out h3{margin:0;font-size:22px;line-height:1.2;letter-spacing:0}
.csg__out h3 span{font-size:15px;color:var(--mute);font-weight:400;margin-left:6px}
.csg__out p{margin:7px 0 0;font-size:14.5px;line-height:1.45}
.csg__out .warn{color:#8a3b1d}
.csg__out .price{font-weight:700}
.csg__cta{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;min-height:50px;border-radius:999px;border:0;background:var(--ink);color:#f7f3ee;font:inherit;font-size:16px;font-weight:600;text-decoration:none;cursor:pointer;padding:12px 20px}
.csg__cta:hover{background:#000}
.csg svg .f{fill:url(#csgFab)}.csg svg .p{fill:#b2977a}.csg svg .p2{fill:#cbb89f}.csg svg .p3{fill:#f5f0e8}.csg svg .d{fill:#ebe3d7}
.csg svg .w{fill:#5e4636}.csg svg .o{fill:url(#csgWood)}.csg svg .o2{fill:url(#csgWood2)}.csg svg .k{fill:#3a2e25}
.csg svg .b1{fill:#7f8b7b}.csg svg .b2{fill:#b26a4b}.csg svg .b3{fill:#e2d5be}.csg svg .bk{fill:#c7a476}
.csg svg .v{fill:#e9e3d9}.csg svg .lb{fill:#e8e0d3}.csg svg .ls{fill:#f8f2e6}.csg svg .ic-a{fill:#c9a882}
.csg svg .f,.csg svg .p,.csg svg .p2,.csg svg .p3,.csg svg .d,.csg svg .w,.csg svg .o,.csg svg .o2,.csg svg .bk,.csg svg .v,.csg svg .lb,.csg svg .ls,.csg svg .b1,.csg svg .b2,.csg svg .b3,.csg svg .ic-a,.csg svg .s
 {stroke:#2c2722;stroke-opacity:.5;stroke-width:1;vector-effect:non-scaling-stroke}
.csg svg .s{fill:none}
.csg__chip[aria-pressed=true] svg .s{stroke:#f7f3ee}
.csg svg .k{stroke:none}
.csg__furn{transition:opacity .22s ease}
.csg .dim{stroke:#2c2722;stroke-opacity:.72;stroke-width:1;vector-effect:non-scaling-stroke;fill:none}
.csg .dim2{stroke:#2c2722;stroke-opacity:.45;stroke-width:1;vector-effect:non-scaling-stroke;fill:none}
.csg svg text{letter-spacing:0}
.csg .txt{fill:#2c2722;font-family:var(--font-body-family,sans-serif);font-weight:600;paint-order:stroke;stroke:#ebe3d8;stroke-linejoin:round}
.csg .txt2{fill:#2c2722;fill-opacity:.7;font-family:var(--font-body-family,sans-serif);font-weight:500}
.csg .cmp rect{fill:none;vector-effect:non-scaling-stroke}.csg .cmp .cu{stroke:#14110e;stroke-opacity:.45;stroke-width:3}.csg .cmp .co{stroke:#fff;stroke-opacity:.95;stroke-width:1.3}
.csg .cmp text{fill:#fff;font-family:var(--font-body-family,sans-serif);font-weight:700;paint-order:stroke;stroke:rgba(20,17,14,.55);stroke-linejoin:round}
.csg .cmp{transition:opacity .25s}
dialog.csg-sheet{padding:0;border:0;color:#2c2722;background:#f7f3ee;width:100%;max-width:100%;max-height:94dvh;margin:auto 0 0;border-radius:18px 18px 0 0;overflow:auto;overscroll-behavior:contain}
dialog.csg-sheet[open]{animation:csgUp .42s cubic-bezier(.2,.85,.25,1)}
dialog.csg-sheet::backdrop{background:rgba(20,17,14,.55);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px)}
@media (min-width:750px){dialog.csg-sheet{width:min(1100px,94vw);max-height:92dvh;margin:auto;border-radius:18px}
 dialog.csg-sheet[open]{animation:csgIn .32s cubic-bezier(.2,.85,.25,1)}}
@keyframes csgUp{from{transform:translateY(40%);opacity:.3}to{transform:none;opacity:1}}
@keyframes csgIn{from{transform:translateY(16px) scale(.98);opacity:0}to{transform:none;opacity:1}}
.csg-sheet__bar{position:sticky;top:0;z-index:3;display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:rgba(247,243,238,.94);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);border-bottom:1px solid rgba(44,39,34,.1)}
.csg-sheet__bar b{font-size:16px;letter-spacing:.02em}
.csg-sheet__x{width:36px;height:36px;border-radius:50%;border:1px solid rgba(44,39,34,.18);background:#fff;font-size:20px;line-height:1;cursor:pointer;color:#2c2722}
.csg-sheet__body{padding:14px 16px 0}
.csg[data-csg=sheet] .csg__stage{position:sticky;top:61px;z-index:2}
@media (min-width:750px){.csg[data-csg=sheet] .csg__grid{grid-template-columns:minmax(0,1.5fr) minmax(300px,1fr);align-items:start}
 .csg[data-csg=sheet] .csg__stage{top:73px}}
.csg__ctabar{padding:4px 0 0}
.csg[data-csg=sheet] .csg__ctabar{position:sticky;bottom:0;z-index:2;padding:12px 0 calc(12px + env(safe-area-inset-bottom));background:linear-gradient(rgba(247,243,238,0),#f7f3ee 30%)}
html.csg-lock,html.csg-lock body{overflow:hidden}
`;
  function injectCSS() {
    if (document.getElementById('csg-css')) return;
    const s = document.createElement('style'); s.id = 'csg-css'; s.textContent = CSS; document.head.appendChild(s);
  }

  /* ---------- the guide ---------- */
  let uid = 0;
  class Guide {
    constructor(root, cfg) {
      this.root = root; this.cfg = cfg; this.id = 'csg' + (++uid);
      this.styles = (cfg.styles || []).filter(s => s.v && s.v.length);
      if (!this.styles.length) return;
      const s0 = this.styles[0], o = s0.opts || [];
      this.iC = o.indexOf('Number of cats'); this.iS = o.indexOf('Choose your size'); if (this.iS < 0) this.iS = o.indexOf('Size');
      this.iF = o.indexOf('Framing options');
      this.st = { style: 0, furn: 'sofa', cats: this.iC >= 0 ? '1 cat' : '', size: '', frame: this.iF >= 0 ? 'Unframed' : '', cmp: false };
      this.cur = null; this.aspectOk = {};
      this.build(); this.set(cfg.start || {}, true);
      this.ro = new ResizeObserver(() => this.layout()); this.ro.observe(this.svg);
    }
    get S() { return this.styles[this.st.style]; }
    vs(filter) { const s = this.S; return s.v.filter(v => filter(v)); }
    o(v, i) { return i >= 0 ? v[1 + i] : ''; }
    catsList() { if (this.iC < 0) return []; return [...new Set(this.S.v.map(v => this.o(v, this.iC)))]; }
    sizesFor(cats) {
      const seen = new Set(), out = [];
      for (const v of this.S.v) { if (this.iC >= 0 && this.o(v, this.iC) !== cats) continue; const s = this.o(v, this.iS); if (!seen.has(s)) { seen.add(s); out.push(s); } }
      return out.filter(parse).sort((a, b) => { const A = parse(a), B = parse(b); return A[0] * A[1] - B[0] * B[1]; });
    }
    framesFor(cats, size) {
      if (this.iF < 0) return [];
      return [...new Set(this.S.v.filter(v => (this.iC < 0 || this.o(v, this.iC) === cats) && this.o(v, this.iS) === size).map(v => this.o(v, this.iF)))];
    }
    variant() {
      return this.S.v.find(v => (this.iC < 0 || this.o(v, this.iC) === this.st.cats) && this.o(v, this.iS) === this.st.size && (this.iF < 0 || this.o(v, this.iF) === this.st.frame));
    }
    art() {
      const s = this.S, d = parse(this.st.size) || [24, 36];
      if (this.st.cats === '2 cats' && s.art2) return [s.art2, 4 / 3];
      if (this.st.cats === '3 cats' && s.art3) return [s.art3, 4 / 3];
      if (Math.abs(d[0] / d[1] - .8) < .01 && s.art45) return [s.art45, .8];
      return [s.art1, 2 / 3];
    }
    money(cents) {
      const v = cents / 100;
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.cfg.currency || 'USD', minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(v); }
      catch (e) { return '$' + v; }
    }

    build() {
      injectCSS();
      const id = this.id, sheet = this.cfg.mode === 'sheet';
      this.root.innerHTML = `
<div class="csg__grid">
  <div class="csg__stage">
    <svg class="csg__svg" role="img" aria-label="Size guide drawing" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="${id}w" x1="0" x2="1" y1="0" y2="0"><stop offset="0" stop-color="#efe7dc"/><stop offset="1" stop-color="#e1d7ca"/></linearGradient>
        <radialGradient id="${id}l" cx=".16" cy=".12" r=".75"><stop offset="0" stop-color="#fff7e8" stop-opacity=".85"/><stop offset=".55" stop-color="#fff7e8" stop-opacity=".18"/><stop offset="1" stop-color="#fff7e8" stop-opacity="0"/></radialGradient>
        <linearGradient id="${id}f" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#b99c7c"/><stop offset="1" stop-color="#a5876a"/></linearGradient>
        <linearGradient id="csgFab" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#e6dbcd"/><stop offset="1" stop-color="#cfc1b0"/></linearGradient>
        <linearGradient id="csgWood" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#caa781"/><stop offset="1" stop-color="#b48f6a"/></linearGradient>
        <linearGradient id="csgWood2" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#d0ae89"/><stop offset="1" stop-color="#bf9b75"/></linearGradient>
        <filter id="${id}fs" x="-20%" y="-20%" width="140%" height="140%"><feColorMatrix type="matrix" values="0 0 0 0 .1  0 0 0 0 .07  0 0 0 0 .05  0 0 0 .28 0"/><feGaussianBlur stdDeviation="1.6"/><feOffset dx="1.6" dy=".6"/></filter>
        <radialGradient id="${id}cs"><stop offset="0" stop-color="#1b1611" stop-opacity=".35"/><stop offset="1" stop-color="#1b1611" stop-opacity="0"/></radialGradient>
        <filter id="${id}s" x="-40%" y="-40%" width="180%" height="190%"><feGaussianBlur stdDeviation="1.1"/></filter>
        <filter id="${id}c" x="-40%" y="-40%" width="180%" height="190%"><feGaussianBlur stdDeviation="2.2"/></filter>
      </defs>
      <rect x="-2000" y="-2000" width="4000" height="2000" fill="url(#${id}w)"/>
      <rect class="csg-light" fill="url(#${id}l)"/>
      <rect x="-2000" y="-2000" width="4000" height="${2000 - CEIL}" fill="#f3eee7"/>
      <line class="dim2" x1="-2000" y1="${-CEIL}" x2="2000" y2="${-CEIL}"/>
      <line class="dim2" x1="-2000" y1="${-(CEIL - 1.6)}" x2="2000" y2="${-(CEIL - 1.6)}"/>
      <text class="txt2 csg-ceil" text-anchor="start">8 ft ceiling</text>
      <rect x="-2000" y="0" width="4000" height="60" fill="url(#${id}f)"/>
      <rect x="-2000" y="-4.5" width="4000" height="4.5" fill="#f4efe8"/>
      <line class="dim2" x1="-2000" y1="-4.5" x2="2000" y2="-4.5"/>
      <line class="dim2" x1="-2000" y1="0" x2="2000" y2="0"/>
      <g class="csg-scale"><line class="dim"/><line class="dim"/><line class="dim"/><text class="txt2">1 ft · true scale</text></g>
      <g class="csg__furn"><ellipse class="csg-cs" cx="0" cy="0" rx="40" ry="2.2" fill="url(#${id}cs)"/><use class="csg-fsh" href="#${id}fg" filter="url(#${id}fs)"/><g id="${id}fg"></g></g>
      <g class="csg-art">
        <rect class="csg-sh2" fill="#2c2722" fill-opacity=".16" filter="url(#${id}c)"/>
        <rect class="csg-sh" fill="#1b1611" fill-opacity=".38" filter="url(#${id}s)"/>
        <rect class="csg-frame"/>
        <rect class="csg-gap" fill="#14110e"/>
        <rect class="csg-back" fill="#2c2722"/>
        <image class="csg-img" preserveAspectRatio="xMidYMid slice"/>
        <rect class="csg-edge" fill="none" stroke="#000" stroke-opacity=".22" stroke-width="1" vector-effect="non-scaling-stroke"/>
      </g>
      <g class="cmp" opacity="0"></g>
      <g class="csg-dims"></g>
    </svg>
    <button type="button" class="csg__cmpbtn" aria-pressed="false">Compare sizes</button>
  </div>
  <div class="csg__panel">
    ${!sheet && this.styles.length > 1 ? `<div class="csg__row"><p class="csg__lab">Style</p><div class="csg__chips" data-k="style"></div></div>` : ''}
    <div class="csg__row"><p class="csg__lab">Hangs above</p><div class="csg__chips" data-k="furn"></div></div>
    ${this.iC >= 0 ? `<div class="csg__row"><p class="csg__lab">Number of cats</p><div class="csg__chips" data-k="cats"></div></div>` : ''}
    <div class="csg__row"><p class="csg__lab">Size</p><div class="csg__chips" data-k="size"></div></div>
    ${this.iF >= 0 ? `<div class="csg__row"><p class="csg__lab">Frame</p><div class="csg__chips" data-k="frame"></div></div>` : ''}
    <div class="csg__out" aria-live="polite"></div>
    <div class="csg__ctabar"><a class="csg__cta" href="#"></a></div>
  </div>
</div>`;
      const q = s => this.root.querySelector(s);
      this.svg = q('svg'); this.gF = q('.csg__furn'); this.gFi = this.gF.querySelector('g'); this.gCs = this.gF.querySelector('.csg-cs'); this.gCmp = q('.cmp'); this.gDims = q('.csg-dims');
      this.el = { light: q('.csg-light'), ceil: q('.csg-ceil'), sh: q('.csg-sh'), sh2: q('.csg-sh2'), frame: q('.csg-frame'), gap: q('.csg-gap'), back: q('.csg-back'), img: q('.csg-img'), edge: q('.csg-edge'), scale: q('.csg-scale') };
      this.out = q('.csg__out'); this.cta = q('.csg__cta'); this.cmpBtn = q('.csg__cmpbtn');
      this.cmpBtn.onclick = () => { this.st.cmp = !this.st.cmp; this.cmpBtn.setAttribute('aria-pressed', this.st.cmp); this.drawCmp(); };
      this.root.addEventListener('click', e => {
        const b = e.target.closest('.csg__chip'); if (!b || b.disabled || !this.root.contains(b)) return;
        const k = b.parentNode.dataset.k, v = b.dataset.v;
        this.set({ [k]: k === 'style' ? +v : v });
      });
      this.cta.addEventListener('click', e => { if (this.cfg.mode === 'sheet') { e.preventDefault(); this.root.dispatchEvent(new CustomEvent('csg:apply', { bubbles: true, detail: { ...this.st } })); } });
    }

    /* keep the selection valid: cats -> sizes -> frames */
    set(p, instant) {
      const st = this.st, prevFurn = st.furn;
      Object.assign(st, Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '')));
      if (st.style >= this.styles.length) st.style = 0;
      const cl = this.catsList(); if (this.iC >= 0 && !cl.includes(st.cats)) st.cats = cl[0];
      const sl = this.sizesFor(st.cats);
      if (!sl.includes(st.size)) {
        const pk = (PICKS[st.furn] || []).map(k => sl.find(s => kOf(s) === k)).filter(Boolean);
        st.size = pk[0] || sl[Math.min(sl.length - 1, 3)] || sl[0];
      }
      const fl = this.framesFor(st.cats, st.size);
      if (this.iF >= 0 && !fl.includes(st.frame)) st.frame = fl.includes('Unframed') ? 'Unframed' : fl[0];
      if (!FURN.find(f => f.k === st.furn)) st.furn = 'sofa';
      this.renderControls();
      if (prevFurn !== st.furn || !this.gFi.innerHTML) this.swapFurn(instant);
      this.target(instant);
      this.renderOut();
    }

    renderControls() {
      const st = this.st, picks = PICKS[st.furn] || [];
      const put = (k, html) => { const box = this.root.querySelector(`.csg__chips[data-k="${k}"]`); if (box) box.innerHTML = html; };
      put('style', this.styles.map((s, i) => `<button type="button" class="csg__chip" data-v="${i}" aria-pressed="${i === st.style}">${esc(s.name)}</button>`).join(''));
      put('furn', FURN.map(f => `<button type="button" class="csg__chip csg__chip--fu" data-v="${f.k}" aria-pressed="${f.k === st.furn}">${icon(f.k)}${f.label}</button>`).join(''));
      put('cats', this.catsList().map(c => `<button type="button" class="csg__chip" data-v="${esc(c)}" aria-pressed="${c === st.cats}">${esc(c)}</button>`).join(''));
      put('size', this.sizesFor(st.cats).map(s => { const d = parse(s);
        return `<button type="button" class="csg__chip csg__chip--sz" data-v="${esc(s)}" aria-pressed="${s === st.size}">${picks.includes(kOf(s)) ? '<span class="pick">Best fit</span>' : ''}${nice(s)} in<small>${cm(d[0])} × ${cm(d[1])} cm</small></button>`; }).join(''));
      const fl = this.framesFor(st.cats, st.size), all = this.iF >= 0 ? [...new Set(this.S.v.map(v => this.o(v, this.iF)))] : [];
      put('frame', all.map(f => `<button type="button" class="csg__chip" data-v="${esc(f)}" aria-pressed="${f === st.frame}" ${fl.includes(f) ? '' : 'disabled title="Not offered in this size"'}><span class="csg__sw${FRAMES[f] ? '' : ' csg__sw--u'}" style="${FRAMES[f] ? 'background:' + FRAMES[f] : ''}"></span>${esc(f === 'Unframed' ? 'Gallery wrap' : f.replace(' frame', ''))}</button>`).join(''));
      for (const b of this.root.querySelectorAll('.csg__chip[aria-pressed=true]')) {
        const box = b.parentNode; if (box.scrollWidth > box.clientWidth) { const l = b.offsetLeft - box.clientWidth / 2 + b.offsetWidth / 2; box.scrollTo({ left: l, behavior: reduce ? 'auto' : 'smooth' }); }
      }
    }

    swapFurn(instant) {
      const html = furn(this.st.furn), f = FURN.find(x => x.k === this.st.furn);
      const put = () => { this.gFi.innerHTML = html; this.gCs.setAttribute('rx', f.w * .56); this.gCs.style.display = f.w ? '' : 'none'; this.gF.style.opacity = 1; };
      if (instant || reduce) return put();
      clearTimeout(this._ft); this.gF.style.opacity = 0;
      this._ft = setTimeout(put, 200);
    }

    hang(w, h, f) {
      return f.k === 'wall' ? CENTRE : Math.max(CENTRE, f.top + GAP + h / 2);
    }

    target(instant) {
      const st = this.st, d = parse(st.size) || [24, 36], f = FURN.find(x => x.k === st.furn);
      const framed = !!FRAMES[st.frame], fr = framed ? 0.78 : 0;
      const ow = d[0] + 2 * fr, oh = d[1] + 2 * fr, cy = this.hang(ow, oh, f);
      const [href, asp] = this.art();
      if (this.el.img.getAttribute('href') !== href) {
        this.el.img.setAttribute('href', href);
        const im = new Image(); im.onload = () => {
          const ok = Math.abs(im.naturalWidth / im.naturalHeight - d[0] / d[1]) < .02;
          this.el.img.setAttribute('preserveAspectRatio', ok ? 'xMidYMid slice' : 'xMidYMid meet');
        }; im.src = href;
      }
      if (framed) this.el.frame.setAttribute('fill', FRAMES[st.frame]);
      const to = { w: d[0], h: d[1], fr, cy, fw: f.w };
      if (!this.cur || instant || reduce) { this.cur = { ...to }; this.layout(); return; }
      this.from = { ...this.cur }; this.to = to; this.t0 = performance.now();
      if (!this.raf) { const tick = now => { const t = Math.min(1, (now - this.t0) / 560), e = ease(t);
          for (const k in this.to) this.cur[k] = this.from[k] + (this.to[k] - this.from[k]) * e;
          this.layout(); this.raf = t < 1 ? requestAnimationFrame(tick) : 0; };
        this.raf = requestAnimationFrame(tick); }
      this.drawCmp();
    }

    layout() {
      const c = this.cur; if (!c) return;
      const cw = this.svg.clientWidth || 600, ch = this.svg.clientHeight || 480;
      const ow = c.w + 2 * c.fr, oh = c.h + 2 * c.fr;
      const bottom = -12; let top = Math.max(CEIL + 12, c.cy + oh / 2 + 18);
      let vbH = top - bottom, vbW = vbH * cw / ch;
      const need = Math.max(c.fw + 22, ow + 44, 64);
      if (vbW < need) { vbW = need; vbH = vbW * ch / cw; }
      const x0 = -vbW / 2, y0 = -(bottom + vbH);
      this.svg.setAttribute('viewBox', `${x0.toFixed(2)} ${y0.toFixed(2)} ${vbW.toFixed(2)} ${vbH.toFixed(2)}`);
      const k = vbW / cw; this.k = k; this.vb = [x0, y0, vbW, vbH];
      const L_ = this.el;
      L_.light.setAttribute('x', x0); L_.light.setAttribute('y', y0); L_.light.setAttribute('width', vbW); L_.light.setAttribute('height', vbH);
      L_.ceil.setAttribute('x', x0 + 12 * k); L_.ceil.setAttribute('y', -CEIL - 6 * k); L_.ceil.setAttribute('font-size', 11 * k);
      L_.ceil.style.display = -CEIL > y0 + 14 * k ? '' : 'none';
      // scale bar on the floor
      const sx = x0 + 14 * k, sy = 6.5, [l1, l2, l3] = L_.scale.querySelectorAll('line'), st = L_.scale.querySelector('text');
      l1.setAttribute('x1', sx); l1.setAttribute('x2', sx + 12); l1.setAttribute('y1', sy); l1.setAttribute('y2', sy);
      for (const [ln, x] of [[l2, sx], [l3, sx + 12]]) { ln.setAttribute('x1', x); ln.setAttribute('x2', x); ln.setAttribute('y1', sy - 3 * k); ln.setAttribute('y2', sy + 3 * k); }
      st.setAttribute('x', sx + 12 + 6 * k); st.setAttribute('y', sy + 3.6 * k); st.setAttribute('font-size', 11 * k);
      st.setAttribute('fill', '#2c2722');
      // canvas
      const ax = -c.w / 2, ay = -(c.cy + c.h / 2), fx = -ow / 2, fy = -(c.cy + oh / 2);
      const set = (el, x, y, w, h) => { el.setAttribute('x', r1(x * 100) / 100); el.setAttribute('y', r1(y * 100) / 100); el.setAttribute('width', Math.max(0, w)); el.setAttribute('height', Math.max(0, h)); };
      set(L_.sh, fx + .35, fy + 1.1, ow, oh); set(L_.sh2, fx + 1.2, fy + 3, ow, oh);
      set(L_.frame, fx, fy, ow, oh); L_.frame.style.display = c.fr > .01 ? '' : 'none';
      const g = .25 * Math.min(1, c.fr * 4);
      set(L_.gap, ax - g, ay - g, c.w + 2 * g, c.h + 2 * g); L_.gap.style.display = c.fr > .01 ? '' : 'none';
      set(L_.back, ax, ay, c.w, c.h); set(L_.img, ax, ay, c.w, c.h); set(L_.edge, ax, ay, c.w, c.h);
      this.drawDims(ow, oh, fx, fy, k);
      if (this.st.cmp) this.drawCmp();
    }

    drawDims(ow, oh, fx, fy, k) {
      const st = this.st, d = parse(st.size) || [24, 36], framed = !!FRAMES[st.frame];
      const tw = d[0] + (framed ? 1.5 : 0), th = d[1] + (framed ? 1.5 : 0);
      const off = 11 * k, tk = 4 * k, fs = 11.5 * k, sw = 3.2 * k;
      const [x0, , vbW] = this.vb, right = fx + ow, cy = this.cur.cy;
      const yT = fy - off;
      let s = `<line class="dim" x1="${fx}" y1="${yT}" x2="${right}" y2="${yT}"/>` +
        `<line class="dim" x1="${fx}" y1="${yT - tk}" x2="${fx}" y2="${yT + tk}"/><line class="dim" x1="${right}" y1="${yT - tk}" x2="${right}" y2="${yT + tk}"/>` +
        `<text class="txt" x="0" y="${yT - 5 * k}" font-size="${fs}" stroke-width="${sw}" text-anchor="middle">${tw} in · ${cm(tw)} cm</text>`;
      const roomR = x0 + vbW - right, onRight = roomR > 30 * k;
      const xH = onRight ? right + off : fx - off, yb = fy + oh;
      s += `<line class="dim" x1="${xH}" y1="${fy}" x2="${xH}" y2="${yb}"/>` +
        `<line class="dim" x1="${xH - tk}" y1="${fy}" x2="${xH + tk}" y2="${fy}"/><line class="dim" x1="${xH - tk}" y1="${yb}" x2="${xH + tk}" y2="${yb}"/>` +
        (() => { const t = `${th} in · ${cm(th)} cm`, tx = xH + (onRight ? 6 : -6) * k, ty = fy + oh / 2;
          return t.length * .6 * fs > oh - 2 * k
            ? `<text class="txt" x="${tx}" y="${ty}" font-size="${fs}" stroke-width="${sw}" text-anchor="${onRight ? 'start' : 'end'}" dominant-baseline="central">${t}</text>`
            : `<text class="txt" x="${tx}" y="${ty}" font-size="${fs}" stroke-width="${sw}" text-anchor="middle" dominant-baseline="${onRight ? 'hanging' : 'auto'}" transform="rotate(-90 ${tx} ${ty})">${t}</text>`; })();
      // floor to centre
      const xc = onRight ? fx - off : right + off;
      s += `<line class="dim2" x1="${xc}" y1="0" x2="${xc}" y2="${-cy}" stroke-dasharray="${3 * k} ${3 * k}"/>` +
        `<line class="dim" x1="${xc - tk}" y1="${-cy}" x2="${xc + tk}" y2="${-cy}"/><line class="dim2" x1="${xc - tk}" y1="0" x2="${xc + tk}" y2="0"/>` +
        `<text class="txt" x="${xc + (onRight ? -6 : 6) * k}" y="${-cy + 4 * k}" font-size="${fs * .92}" stroke-width="${sw}" text-anchor="${onRight ? 'end' : 'start'}">${half(this.hang(tw, th, FURN.find(f => f.k === st.furn)))} in to center</text>`;
      this.gDims.innerHTML = s;
    }

    drawCmp() {
      const on = this.st.cmp; this.gCmp.setAttribute('opacity', on ? 1 : 0);
      if (!on || !this.k) return;
      const k = this.k, f = FURN.find(x => x.k === this.st.furn);
      const sizes = this.sizesFor(this.st.cats).filter(s => s !== this.st.size);
      this.gCmp.innerHTML = sizes.map((s, i) => { const [w, h] = parse(s), cy = this.hang(w, h, f), x = -w / 2, y = -(cy + h / 2);
        const left = i % 2 === 0;
        return `<rect class="cu" x="${x}" y="${y}" width="${w}" height="${h}"/><rect class="co" x="${x}" y="${y}" width="${w}" height="${h}" stroke-dasharray="${5 * k} ${3.5 * k}"/>` +
          `<text x="${left ? x + 3 * k : x + w - 3 * k}" y="${y + 12 * k}" font-size="${10 * k}" stroke-width="${3 * k}" text-anchor="${left ? 'start' : 'end'}">${nice(s)}</text>`; }).join('');
    }

    renderOut() {
      const st = this.st, d = parse(st.size) || [24, 36], f = FURN.find(x => x.k === st.furn);
      const framed = !!FRAMES[st.frame], ow = d[0] + (framed ? 1.5 : 0), oh = d[1] + (framed ? 1.5 : 0);
      const cy = this.hang(ow, oh, f), top = cy + oh / 2, bot = cy - oh / 2, fl = this.framesFor(st.cats, st.size);
      const v = this.variant(), price = v ? this.money(v[5]) : '';
      let h = `<h3>${nice(st.size)} in<span>${cm(d[0])} × ${cm(d[1])} cm</span></h3>`;
      if (framed) h += `<p>${esc(st.frame)}: ${ow} × ${oh} in overall (${cm(ow)} × ${cm(oh)} cm). The frame adds about ¾ in on each side.</p>`;
      else if (fl.some(x => FRAMES[x])) h += `<p>Canvas only. A frame adds about 1 in on each side.</p>`;
      else if (this.iF >= 0) h += `<p>Canvas only. This size is not offered framed.</p>`;
      if (f.k === 'wall') h += `<p>On an open wall: center about ${half(cy)} in from the floor, the usual eye level for art.</p>`;
      else h += `<p>Over ${f.desc}: bottom edge about ${half(bot - f.top)} in above ${f.the}, center ${half(cy)} in from the floor. It spans ${Math.round(ow / f.w * 100)}% of the width.</p>`;
      if (top > CEIL - 3) {
        const alt = this.sizesFor(st.cats).filter(s => { const [w, hh] = parse(s); return this.hang(w, hh, f) + hh / 2 <= CEIL - 3; }).pop();
        h += `<p class="warn">The top edge reaches about ${Math.round(top)} in. Under an 8 ft (96 in) ceiling that is too tight here${alt ? `; ${nice(alt)} in fits better` : ''}.</p>`;
      }
      if (price) h += `<p><span class="price">${price}</span>${this.cfg.mode === 'sheet' ? '' : ' · ' + esc(this.S.name)}</p>`;
      this.out.innerHTML = h;
      this.svg.setAttribute('aria-label', `${nice(st.size)} inch canvas${framed ? ' in a ' + st.frame.toLowerCase() : ''} hung over ${f.desc}, drawn to scale.`);
      if (this.cfg.mode === 'sheet') this.cta.textContent = `Choose ${nice(st.size)} in${framed ? ', ' + st.frame.toLowerCase() : ''}`;
      else { this.cta.textContent = `Shop ${this.S.name} in ${nice(st.size)} in`; this.cta.href = this.S.url + (v ? '?variant=' + v[0] : ''); }
    }
  }

  /* ---------- product-page sheet ---------- */
  const pickNow = n => { const c = document.querySelector(`variant-selects input[data-option-name="${n}"]:checked`); return c ? c.value : ''; };
  const sleep = ms => new Promise(r => setTimeout(r, ms));
  async function apply(st) {
    for (const [name, val] of [['Number of cats', st.cats], ['Choose your size', st.size], ['Framing options', st.frame]]) {
      if (!val || pickNow(name) === val) continue;
      const inp = [...document.querySelectorAll(`variant-selects input[data-option-name="${name}"]`)].find(i => i.value === val);
      if (!inp) continue;
      inp.click();
      for (let t = 0; t < 30; t++) { await sleep(80); const now = document.querySelector(`variant-selects input[data-option-name="${name}"]:checked`); if (now && now !== inp && now.value === val) break; }
      await sleep(120);
    }
  }
  function openSheet() {
    let d = [...document.body.children].find(e => e.matches && e.matches('dialog[data-csg-sheet]')) || document.querySelector('dialog[data-csg-sheet]');
    if (!d) { location.href = '/pages/size-guide'; return; }
    if (d.parentNode !== document.body) document.body.appendChild(d);
    for (const o of document.querySelectorAll('dialog[data-csg-sheet]')) if (o !== d && o.parentNode === document.body) o.remove();
    const start = { cats: pickNow('Number of cats'), size: pickNow('Choose your size'), frame: pickNow('Framing options') };
    if (!d._g) {
      injectCSS();
      const cfg = JSON.parse(d.querySelector('[data-csg-config]').textContent);
      d._g = new Guide(d.querySelector('[data-csg]'), { ...cfg, start });
      d.querySelector('[data-csg-close]').onclick = () => d.close();
      d.addEventListener('close', () => document.documentElement.classList.remove('csg-lock'));
      d.addEventListener('click', e => { if (e.target === d) d.close(); });
      d.addEventListener('csg:apply', async e => { d.close(); await apply(e.detail); });
    } else d._g.set(start, true);
    document.documentElement.classList.add('csg-lock');
    d.showModal();
    requestAnimationFrame(() => d._g.layout());
  }
  document.addEventListener('click', e => { const b = e.target.closest('[data-csg-open]'); if (!b || !document.querySelector('dialog[data-csg-sheet]')) return; e.preventDefault(); openSheet(); });

  /* ---------- standalone page ---------- */
  function boot() {
    for (const root of document.querySelectorAll('[data-csg="page"]')) {
      if (root._g) continue;
      const c = root.querySelector('[data-csg-config]'); if (!c) continue;
      const cfg = JSON.parse(c.textContent), u = new URLSearchParams(location.search);
      const start = {};
      if (u.get('style')) { const i = cfg.styles.findIndex(s => s.key === u.get('style')); if (i >= 0) start.style = i; }
      if (u.get('over')) start.furn = u.get('over');
      if (u.get('size')) start.size = (cfg.styles[0].v.find(v => kOf(v[2]) === u.get('size')) || [])[2];
      root._g = new Guide(root, { ...cfg, start });
    }
  }
  window.csSizeGuide = { Guide, boot, openSheet };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
